import type { IntlShape } from 'react-intl';

import { MentionProvider, MentionResource } from '@atlaskit/mention';
import { SLI_EVENT_TYPE } from '@atlaskit/mention/resource';
import {
  MentionContextIdentifier,
  MentionDescription,
  MentionsResult,
} from '@atlaskit/mention/types';

import { verifyRepoMember } from 'src/redux/pull-request/actions';
import { bitbucketStore } from 'src/store';
import { urls } from 'src/urls';
import { messages } from 'src/utils/bb-mentions-provider.i18n';
import { hasDistinctDisplayName } from 'src/utils/display-name-with-nickname';
import { authRequest, jsonHeaders } from 'src/utils/fetch';

import { uncurlyAaid } from '../../../utils/uncurly-aaid';
import { ConversationsMentionContext } from '../../bbc-atlaskit-editor/src/types/ConversationsMentionContext';

const INITIAL_STATE = 'initialState';
const SEARCH = 'searchUser';
const SUCCEEDED = 'succeeded';
const FAILED = 'failed';
const CONTEXT_TYPE = 'BitbucketMentions';
const PRODUCT = 'bitbucket';
const MAX_RESULTS = 20;

const URS_GATEWAY_URL = '/gateway/api/v1/recommendations';

const BB_SEARCH_QUERY_FILTER =
  'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)';

// @ts-ignore TODO: fix noImplicitAny error here
export const transformUserToMention = rawUser => {
  const mentionData = {
    id: `{${rawUser.mention_id}}`,
    avatarUrl: rawUser.avatar_url,
    name: rawUser.display_name,
    mentionName: rawUser.username,
    nickname: undefined,
    lozenge: rawUser.is_teammate ? { text: 'teammate' } : undefined,
  };

  if (hasDistinctDisplayName(rawUser.display_name, rawUser.nickname)) {
    mentionData.nickname = rawUser.nickname;
  }

  return mentionData;
};

export type MentionsBuildArgs = {
  owner: string;
  slug: string;
  intl: IntlShape;
};

/**
 * This is an implementation which calls URS to provide a list of recommended users to mention.
 * It is adapted from the mentions provider implemented in the confleunce front end, without the teams part.
 *
 * This extends `MentionResource`, which provides more control and additional parameters used in URS over the `AbstractMentionResource`.
 *
 * The entry point for a `MentionProvider` is its <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/editor/editor-core/src/plugins/mentions/index.tsx#lines-219">filter method</a>.
 * The `MentionResource` then eventually calls <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/elements/mention/src/api/MentionResource.ts#lines-352">`remoteInitialState`</a> for bootstrap/empty queries,
 * and <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/elements/mention/src/api/MentionResource.ts#lines-436">`remoteSearch`</a> for non-empty queries.
 * This implem extends `MentionResource` and overrides those methods to call URS.
 *
 * In case the mention fails, we fall back to the existing bitbucket endpoint.
 */
export class UserRecommendationsMentionProvider
  extends MentionResource
  implements MentionProvider
{
  private bitbucketMentionsFallbackConfig: MentionsBuildArgs;
  private conversationsMentionContext: ConversationsMentionContext;

  constructor(
    bitbucketMentionsFallbackConfig: MentionsBuildArgs,
    conversationsMentionContext: ConversationsMentionContext
  ) {
    super({
      url: URS_GATEWAY_URL,
      productId: 'bitbucket',
    });
    this.bitbucketMentionsFallbackConfig = bitbucketMentionsFallbackConfig;
    this.conversationsMentionContext = conversationsMentionContext;
  }

  /**
   * Overrides `remoteInitialState` from `MentionResource`
   * This is invoked for bootstrap/empty queries.
   * No fallback is required, since the existing behaviour does not return anything for an empty list.
   */
  async remoteInitialState(
    contextIdentifier?: MentionContextIdentifier
  ): Promise<MentionsResult> {
    try {
      const promise = await this.doSearch('', contextIdentifier);
      this.notifyAnalyticsListenersInternal(INITIAL_STATE, SUCCEEDED);
      return promise;
    } catch (e) {
      this.notifyAnalyticsListenersInternal(INITIAL_STATE, FAILED);
      throw e;
    }
  }

  /**
   * Overrides `remoteSearch` from `MentionResource`
   * This is invoked for non-empty queries.
   */
  async remoteSearch(
    query: string,
    contextIdentifier?: MentionContextIdentifier
  ): Promise<MentionsResult> {
    try {
      const promise = await this.doSearch(query, contextIdentifier);
      this.notifyAnalyticsListenersInternal(SEARCH, SUCCEEDED);
      return promise;
    } catch {
      // The initial call to URS has failed. Try with the old BB endpoint.
      // Note: This is important as it maintains the existing CPUS GDPR permission bootstrapping behaviour.
      // It will also allow mentions to continue operating if URS is down.
      try {
        const promise = await this.doSearchFallback(query);
        this.notifyAnalyticsListenersInternal(SEARCH, SUCCEEDED);
        return promise;
      } catch (fallbackException) {
        this.notifyAnalyticsListenersInternal(SEARCH, FAILED);
        throw fallbackException;
      }
    }
  }

  private async doSearch(
    query: string,
    contextIdentifier?: MentionContextIdentifier
  ): Promise<MentionsResult> {
    const response = await this.post(query, contextIdentifier);

    if (!response.ok) {
      const errorBody = await response
        .json()
        .catch(_ => 'error while parsing json');
      throw new Error(JSON.stringify(errorBody));
    }

    const body = await response.json();

    const mentions = body.recommendedUsers.map((rawUser: any) => {
      return UserRecommendationsMentionProvider.transformUserToMention(rawUser);
    });

    return { query, mentions };
  }

  private post(
    query: string,
    contextIdentifier?: MentionContextIdentifier
  ): Promise<Response> {
    const workspaces = new Set().add(
      this.conversationsMentionContext.repositoryWorkspaceUUID
    );

    let isPublicRepo = !this.conversationsMentionContext.isPrivateRepo;
    if (location.hostname.endsWith('devbucket.localhost')) {
      // Testing mentions against devbucket is difficult because CPUS doesn't
      // know anything about the local devbucket workspace. Instead, we use an
      // existing integration workspace (`test-workspace`) and mark it as public
      // so that we can test mentions. As a public repository this should return
      // any integration users, so if you want to test specific permissions you
      // can substitute this with your own integration workspace that already
      // has permissions set up.
      workspaces.add('d83d0d95-a100-4926-bdb7-2a83d54e1708');
      isPublicRepo = true;
    }

    return fetch(
      authRequest(URS_GATEWAY_URL, {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({
          context: {
            contextType: CONTEXT_TYPE,
            principalId: 'context',
            productKey: PRODUCT,
            siteId: PRODUCT, // Bitbucket is tenantless, so we pass `bitbucket` in as the siteId.
            containerId: contextIdentifier && contextIdentifier.containerId, // repo ID
            sessionId: contextIdentifier && contextIdentifier.sessionId,
            productAttributes: {
              // Bitbucket specific attribute fields go here.
              pullRequestId: this.conversationsMentionContext.pullrequestId,
              isPublicRepo,
              workspaceIds: [...workspaces], // send as a list
            },
          },
          searchQuery: {
            queryString: query,
            filter: BB_SEARCH_QUERY_FILTER,
          },
          maxNumberOfResults: MAX_RESULTS,
          includeTeams: false, // This can be enabled to support teams
        }),
      })
    );
  }

  /**
   * The fallback should call the existing BB endpoint for mentions.
   * This is adapted from the existing filter query used in `bb-mentions-provider.ts`
   * @param query string
   * @private
   */
  private async doSearchFallback(query: string): Promise<MentionsResult> {
    const url = urls.ui.repositoryMentions(
      this.bitbucketMentionsFallbackConfig.owner,
      this.bitbucketMentionsFallbackConfig.slug,
      query
    );

    const response = await fetch(authRequest(url));

    if (!response.ok) {
      const errorMessage =
        this.bitbucketMentionsFallbackConfig.intl.formatMessage(messages.error);
      throw new Error(errorMessage);
    }

    const rawResults = await response.json();

    if (rawResults.length === 0) {
      const noMatches = this.bitbucketMentionsFallbackConfig.intl.formatMessage(
        messages.noMatches,
        { query }
      );
      // eslint-disable-next-line no-underscore-dangle
      this._notifyInfoListeners(noMatches);
      // Return empty.
      return { query, mentions: [] };
    }

    // @ts-ignore TODO: fix noImplicitAny error here
    const mentions = rawResults.map(rawUser =>
      UserRecommendationsMentionProvider.transformFallbackUserToMention(rawUser)
    );

    return { query, mentions };
  }

  // Adapted from `bb-mentions-provider`.  We return the same data type, but using what URS returns.
  // See https://hello.atlassian.net/wiki/spaces/SMRT/pages/828023123/Teammate+Lozenge+in+Bitbucket+DECIDED
  // @ts-ignore TODO: fix noImplicitAny error here
  static transformUserToMention = recommended => {
    const lozenge =
      recommended.attributes && recommended.attributes.workspaceMember
        ? { text: 'MEMBER' }
        : undefined;

    const mentionData = {
      id: `{${recommended.id}}`,
      avatarUrl: recommended.avatarUrl,
      name: recommended.name,
      nickname: undefined,
      mentionName: recommended.nickname || '',
      lozenge,
    };

    if (hasDistinctDisplayName(recommended.name, recommended.nickname)) {
      mentionData.nickname = recommended.nickname;
    }
    return mentionData;
  };

  /* The teammate lozenge will not be shown.
   * This is to avoid an inconsistent UI where the results the initial results may fail showing the lozenge
   * but subsequent ones pass.
   */
  // @ts-ignore
  private static transformFallbackUserToMention = rawUser => {
    return {
      ...transformUserToMention(rawUser),
      lozenge: undefined, // Empty out the lozenge field in the fallback to avoid inconsistent UI.
    };
  };

  private notifyAnalyticsListenersInternal(
    actionSubject: string,
    action: string
  ) {
    // eslint-disable-next-line no-underscore-dangle
    this._notifyAnalyticsListeners(SLI_EVENT_TYPE, actionSubject, action);
  }

  // Overrides the mention record function.
  // We fire the selection analytics from the Mention Typeahead UI event.
  // eslint-disable-next-line require-await
  async recordMentionSelection({
    id,
    mentionName,
  }: MentionDescription): Promise<void> {
    const aaid = uncurlyAaid(id);
    if (aaid) {
      bitbucketStore.dispatch(verifyRepoMember(aaid, mentionName));
    }
    return Promise.resolve();
  }
}
